import { useMutation } from "@apollo/client/react";
import {
  Breadcrumbs,
  Button,
  Grid,
  IconButton,
  Link,
  Paper,
  Stack,
  styled,
  Tab,
  Table,
  TableBody,
  TableCell,
  tableCellClasses,
  TableHead,
  TableRow,
  Tabs,
  Typography,
} from "@mui/material";
import { PencilIcon } from "@phosphor-icons/react/dist/csr/Pencil";
import { PlusIcon } from "@phosphor-icons/react/dist/csr/Plus";
import { ProhibitIcon } from "@phosphor-icons/react/dist/csr/Prohibit";
import { UserMinusIcon } from "@phosphor-icons/react/dist/csr/UserMinus";
import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { Link as RouterLink, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { EditableTextField, hasPermission, IfPermission, ListUsersFilters } from "../../../../components";
import { BaseAdminContainer } from "../../../../components/ui/common";
import { EmptyContainer as Empty } from "../../../../components/ui/EmptyContainer";
import Permissions from "../../../../constants/permissions";
import { isRole } from "../../../../constants/roles";
import useAuth from "../../../../context/UserContext";
import {
  AccountDetail as AccountDetailType,
  AccountLoginDetail,
  SaveAccountV2Mutation,
  SaveAccountV2MutationVariables,
  SaveAccountV2Document,
} from "../../../../graphql/types";
import { AddUser } from "../AddUser";
import { DeactivateAccount } from "../DeactivateAccount";
import { DeleteAccountUser } from "../DeleteAccountUser";
import { EditableSelectRoles } from "../EditableSelectRoles/EditableSelectRoles";
import { SettingsTab } from "./SettingsTab";

const DeleteButton = styled(Button)({
  height: "34px",
});
const UsersTable = ({
  users,
  accountUrlName,
  accountName,
}: {
  users: Array<AccountLoginDetail>;
  accountUrlName: string;
  accountName: string;
}) => {
  const [selectedUserToRemove, setSelectedUserToRemove] = useState<{
    name: string;
    email: string;
  } | null>(null);
  if (users.length === 0) {
    return <Empty title="No users" />;
  }
  const rows = users.map((user) => {
    return (
      <TableRow key={user.email} data-testid={`row-user-${user.name}`}>
        <TableCell>{user.name}</TableCell>
        <TableCell>{user.email}</TableCell>
        <TableCell>
          <EditableSelectRoles roles={user.roles.filter(isRole)} email={user.email} urlName={accountUrlName} />
        </TableCell>
        <TableCell>
          <Stack spacing={1.5} direction={"row"}>
            <IconButton
              onClick={() =>
                setSelectedUserToRemove({
                  name: user.name,
                  email: user.email,
                })
              }
              aria-label={`remove user ${user.name}`}
            >
              <UserMinusIcon />
            </IconButton>
          </Stack>
        </TableCell>
      </TableRow>
    );
  });

  return (
    <>
      <Table
        data-testid="user-table"
        stickyHeader
        component={Paper}
        sx={(theme) => ({
          border: `1px solid ${theme.palette.surfaceLight40.main}`,
        })}
      >
        <TableHead>
          <TableRow
            sx={(theme) => ({
              [`& .${tableCellClasses.root}`]: {
                backgroundColor: theme.palette.primary10.main,
              },
            })}
          >
            <TableCell>Name</TableCell>
            <TableCell>Email</TableCell>
            <TableCell>Roles</TableCell>
            <TableCell>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>{rows}</TableBody>
      </Table>
      {selectedUserToRemove ? (
        <DeleteAccountUser
          onClose={() => setSelectedUserToRemove(null)}
          userEmail={selectedUserToRemove.email}
          userName={selectedUserToRemove.name}
          accountUrlName={accountUrlName}
          accountName={accountName}
          onConfirm={() => {
            setSelectedUserToRemove(null);
          }}
        />
      ) : null}
    </>
  );
};

const ListUsersSection = ({
  account,
  setShowModalAddUser,
  users,
  filter,
  setFilter,
}: {
  account: AccountDetailType;
  setShowModalAddUser: (show: boolean) => void;
  users: Array<AccountLoginDetail>;
  filter: {
    roles: string[] | null;
    nameOrEmail: string | null;
  };
  setFilter: React.Dispatch<
    React.SetStateAction<{
      roles: string[] | null;
      nameOrEmail: string | null;
    }>
  >;
}) => {
  return (
    <BaseAdminContainer>
      <Grid container>
        <Grid size={8}>
          <Typography variant="heading.four">Users</Typography>
        </Grid>
        <Grid sx={{ justifyContent: "flex-end", marginLeft: "auto" }}>
          <Button startIcon={<PlusIcon />} onClick={() => setShowModalAddUser(true)} aria-label="Add user">
            User
          </Button>
        </Grid>
      </Grid>
      <ListUsersFilters
        {...filter}
        onChange={setFilter}
        onClear={() => setFilter({ roles: null, nameOrEmail: null })}
      />
      <UsersTable users={users} accountUrlName={account.urlName} accountName={account.name} />
    </BaseAdminContainer>
  );
};

const AccountData = ({
  account,
  filter,
  setFilter,
}: {
  account: AccountDetailType;
  filter: {
    roles: string[] | null;
    nameOrEmail: string | null;
  };
  setFilter: React.Dispatch<
    React.SetStateAction<{
      roles: string[] | null;
      nameOrEmail: string | null;
    }>
  >;
}) => {
  const [isEditing, setIsEditing] = useState({
    accountName: false,
    accountUrlName: false,
  });
  const [showModalAddUser, setShowModalAddUser] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();
  const [saveAccount, saveAccountState] = useMutation<SaveAccountV2Mutation, SaveAccountV2MutationVariables>(
    SaveAccountV2Document,
    {
      onCompleted: (result: SaveAccountV2Mutation) => {
        resolveMutations(result);
      },
      onError: () => {
        toast.error(`Error when saving ${isEditing.accountName ? "account's name" : "url name"}`);
      },
    }
  );

  const [showModalDeactivate, setShowModalDeactivate] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const currentTab = searchParams.get("currentTab");
  useEffect(() => {
    if (!currentTab) {
      const currentParams = Object.fromEntries([...searchParams]);
      setSearchParams({ ...currentParams, currentTab: "users" }, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const resolveMutations = (result: SaveAccountV2Mutation) => {
    if (result.saveAccountV2.__typename === "ResultOk") {
      toast.success(isEditing.accountName ? "Account's name saved" : "Account's url name saved");
      setIsEditing({
        accountName: false,
        accountUrlName: false,
      });
      if (isEditing.accountUrlName) {
        navigate(`/${getValues("accountUrlName").toLocaleLowerCase()}/users`);
      }
    } else if (result.saveAccountV2.__typename === "SaveAccountErrors") {
      result.saveAccountV2.errors?.forEach((error) => {
        if (error?.__typename === "ConflictingNameError") {
          setError("accountName", {
            message: error.message,
          });
        } else if (error?.__typename === "ConflictingUrlNameError") {
          setError("accountUrlName", {
            message: error.message,
          });
        }
      });
    }
  };

  const accountNameRef = useRef<HTMLInputElement>(null);
  const accountUrlNameRef = useRef<HTMLInputElement>(null);
  const { control, getValues, setError, resetField } = useForm({
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: {
      accountName: account.name,
      accountUrlName: account.urlName,
    },
  });
  const users =
    !filter.roles && !filter.nameOrEmail
      ? account.users
      : account.users.filter((user) => {
          let usersFiltered = true;
          if (filter.roles) {
            // check that the user has all the roles selected
            usersFiltered = filter.roles.every((role) => user.roles.includes(role));
          }
          if (usersFiltered && filter.nameOrEmail) {
            usersFiltered =
              user.name.toLowerCase().includes(filter.nameOrEmail.toLowerCase().trim()) ||
              user.email.toLowerCase().includes(filter.nameOrEmail.toLowerCase().trim());
          }
          return usersFiltered;
        });
  const handleKeyDown = async (
    e: React.KeyboardEvent<HTMLDivElement>,
    keyAttribute: "accountName" | "accountUrlName"
  ) => {
    const newValue = getValues(keyAttribute).trim();
    const originalValue = keyAttribute === "accountName" ? account.name : account.urlName;
    if (saveAccountState.loading) return;
    switch (e.key) {
      case "Enter":
        e.preventDefault();
        if (newValue === originalValue) return;
        if (!newValue) {
          setError(keyAttribute, { message: "Required field" });
          return;
        }
        if (keyAttribute === "accountName") {
          saveAccount({
            variables: {
              urlName: account.urlName,
              name: newValue,
            },
            update: (cache, { data }) => {
              if (data?.saveAccountV2.__typename !== "ResultOk") return;
              cache.modify({
                id: cache.identify({
                  __typename: "Query",
                  urlName: account.urlName,
                }),
                fields: {
                  account(oldData) {
                    return {
                      ...oldData,
                      name: newValue,
                    };
                  },
                },
              });
            },
          });
        } else {
          saveAccount({
            variables: {
              urlName: account.urlName,
              newUrlName: newValue,
            },
          });
        }

        break;
      case "Escape":
        if (isEditing.accountName) {
          resetField("accountName", {
            defaultValue: account.name,
          });
          setIsEditing({ ...isEditing, accountName: false });
        } else {
          resetField("accountUrlName", {
            defaultValue: account.urlName,
          });
          setIsEditing({ ...isEditing, accountUrlName: false });
        }
        break;
      default:
        break;
    }
  };
  useEffect(() => {
    if (accountNameRef.current && isEditing.accountName) accountNameRef.current.focus();
    if (accountUrlNameRef.current && isEditing.accountUrlName) accountUrlNameRef.current.focus();
  }, [accountNameRef, accountUrlNameRef, isEditing.accountName, isEditing.accountUrlName]);

  return (
    <>
      <Breadcrumbs>
        <Link component={RouterLink} to="/admin?currentTab=accounts">
          <Typography variant="body.overline.regular">Accounts</Typography>
        </Link>
        <Typography variant="body.overline.bold">{account.urlName}</Typography>
      </Breadcrumbs>
      <BaseAdminContainer>
        <Grid container>
          <Grid container>
            <Grid size={9} sx={{ height: "40px" }}>
              {isEditing.accountName ? (
                <EditableTextField
                  name="accountName"
                  control={control}
                  rules={{ required: true }}
                  onKeyDown={(e) => handleKeyDown(e, "accountName")}
                  inputRef={accountNameRef}
                  onBlur={() => {
                    resetField("accountName", {
                      defaultValue: account.name,
                    });
                    setIsEditing({ ...isEditing, accountName: false });
                  }}
                  isLoading={saveAccountState.loading}
                  onFocus={(e) => e.currentTarget.select()}
                />
              ) : (
                <Grid container sx={{ alignItems: "center" }}>
                  <Typography variant="heading.four" aria-label="account-name-label">
                    {account.name}
                  </Typography>
                  <IfPermission permission={Permissions.ManageAccounts}>
                    <IconButton
                      data-testid="edit-field-accountName"
                      onClick={() => {
                        setIsEditing({
                          accountName: true,
                          accountUrlName: false,
                        });
                      }}
                    >
                      <PencilIcon />
                    </IconButton>
                  </IfPermission>
                </Grid>
              )}
            </Grid>
            <IfPermission permission={Permissions.ManageAccounts}>
              <Grid container size={3} sx={{ justifyContent: "flex-end", marginLeft: "auto" }}>
                <DeleteButton
                  startIcon={<ProhibitIcon />}
                  color={"error"}
                  onClick={() => setShowModalDeactivate(true)}
                  data-testid="deactivate-account"
                >
                  Deactivate
                </DeleteButton>
              </Grid>
            </IfPermission>
          </Grid>
          <Grid container size={9} sx={{ height: "40px" }}>
            {isEditing.accountUrlName ? (
              <EditableTextField
                name="accountUrlName"
                control={control}
                rules={{ required: true }}
                onKeyDown={(e) => handleKeyDown(e, "accountUrlName")}
                inputRef={accountUrlNameRef}
                onBlur={() => {
                  resetField("accountUrlName", {
                    defaultValue: account.urlName,
                  });
                  setIsEditing({ ...isEditing, accountUrlName: false });
                }}
                isLoading={saveAccountState.loading}
                onFocus={(e) => e.currentTarget.select()}
              />
            ) : (
              <Grid container sx={{ alignItems: "center" }}>
                <Typography variant="heading.six" sx={{ color: "text30.main" }} aria-label="account-url-name-label">
                  {account.urlName}
                </Typography>
                <IfPermission permission={Permissions.ManageAccounts}>
                  <IconButton
                    data-testid={"edit-field-accountUrlName"}
                    onClick={() => {
                      setIsEditing({
                        accountName: false,
                        accountUrlName: true,
                      });
                    }}
                  >
                    <PencilIcon />
                  </IconButton>
                </IfPermission>
              </Grid>
            )}
          </Grid>
        </Grid>
      </BaseAdminContainer>
      {hasPermission(Permissions.ManageAccountSettings, user) ? (
        <Tabs
          value={currentTab || false}
          onChange={(event, newValue) => {
            const currentParams = Object.fromEntries([...searchParams]);
            setSearchParams({ ...currentParams, currentTab: newValue });
          }}
          centered
          sx={(theme) => ({ marginTop: theme.spacing(2) })}
        >
          <Tab value="users" label="Users" />
          <Tab value="settings" label="Settings" />
        </Tabs>
      ) : null}
      {currentTab === "settings" && hasPermission(Permissions.ManageAccountSettings, user) ? (
        <SettingsTab dataQuery={account} />
      ) : (
        <ListUsersSection
          account={account}
          setShowModalAddUser={setShowModalAddUser}
          setFilter={setFilter}
          users={users}
          filter={filter}
        />
      )}

      <DeactivateAccount
        showModalDeactivate={showModalDeactivate}
        setShowModalDeactivate={setShowModalDeactivate}
        accountName={account.name}
        accountUrlName={account.urlName}
      />
      <AddUser open={showModalAddUser} onClose={() => setShowModalAddUser(false)} accountUrlName={account.urlName} />
    </>
  );
};

export { AccountData };
